import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Grid,
  Alert,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Popover,
  MenuItem,
  Chip,
} from "@mui/material";
import Iconify from "../components/iconify";
import { del, get, post } from "src/utils/networkUtils";
import GameProviderStatsDialog from "src/components/stage-admin/GameProviderStatsDialog";
import GameProviderGameConfigDialog from "src/components/stage-admin/GameProviderGameConfigDialog";
import DeleteDialog from "src/components/leagues-admin/DeleteDialog";
import { useNavigate } from "react-router-dom";
import { setCurrentGameProvider } from "src/store/slices/gameProviderSlice";
import GameProvidersSpentChart from "src/components/game-providers/GameProvidersSpentChart";
import { getUserPermissions } from "src/utils/permissionsUtils";

export default function GameProvidersPage() {
  const [rows, setRows] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testLink, setTestLink] = useState(null);
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const { isViewer } = getUserPermissions();
  // const [editGameProvider, setEditGameProvider] = useState(null);
  const [showDeleteProviderDialog, setShowDeleteProviderDialog] =
    useState(false);
  const [deleteProviderId, setDeleteProviderId] = useState(null);
  const [showGameProviderIdStats, setShowGameProviderIdStats] = useState(null);
  const [showGameConfigDialog, setShowGameConfigDialog] = useState(null);
  const [spentStats, setSpentStats] = useState(null);
  const [spentStatsLoading, setSpentStatsLoading] = useState(true);
  const [spentStatsDays, setSpentStatsDays] = useState(7);

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const submitGenerateTestUrl = () => {
    setTestLink({
      ...testLink,
      loading: true,
    });
    post(`/admin/game-providers/${testLink.providerId}/generate-test-url`, {
      userId: testLink.userId,
    })
      .then((response) => {
        setTestLink({
          ...testLink,
          testLink: response.url,
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        console.log(error);
        setTestLink({
          ...testLink,
          error: error.response.data.message,
          testLink: null,
          loading: false,
        });
      });
  };

  const openTestLinkHandler = (providerId) => {
    setTestLink({
      providerId,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [gameProvidersResponse] = await Promise.all([
          get(`/admin/game-providers?offset=0&limit=100`),
        ]);

        setRows(gameProvidersResponse.gameProviders);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSpentStatsLoading(true);
    get(`/admin/game-providers/spent-stats?days=${spentStatsDays}`)
      .then((response) => {
        if (cancelled) return;
        setSpentStats(response.stats);
      })
      .catch((error) => {
        console.error("Error fetching provider spend stats:", error);
      })
      .finally(() => {
        if (!cancelled) setSpentStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [spentStatsDays]);
  const deleteProviderHandler = (id) => {
    setLoading(true);
    setOpen(null);
    del(`/admin/game-providers/${id}`).then(() => {
      window.location.reload();
    });
  };
  const openDeleteProviderDialog = (id) => {
    setDeleteProviderId(id);
    setShowDeleteProviderDialog(true);
  };

  const openEditProviderDialog = (id) => {
    if (id) {
      const provider = rows.find((row) => +row.gameProviderId === +id);
      if (provider) {
        dispatch(setCurrentGameProvider(provider));
        navigate(`/dashboard/game-providers/${id}`);
      }
    } else {
      dispatch(setCurrentGameProvider(null));
      navigate(`/dashboard/game-providers/create`);
    }
  };

  const openGameConfigDialog = (id) => {
    setShowGameConfigDialog(id);
    setOpen(null);
  };
  return (
    <>
      <Helmet>
        <title> Game Providers </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h4" gutterBottom>
            Game Providers
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => openEditProviderDialog()}
            >
              Create New Game
            </Button>
          </Box>
        </Stack>

        <GameProvidersSpentChart
          stats={spentStats}
          loading={spentStatsLoading}
          days={spentStatsDays}
          onDaysChange={setSpentStatsDays}
        />

        <Card>
          {loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell>URL</TableCell>

                    <TableCell>Status</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows?.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {row.gameProviderId}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row.ownerType === 0 ? (
                          <Chip
                            label="Low6"
                            color="success"
                            variant="filled"
                            size="small"
                          />
                        ) : row.ownerType === 1 ? (
                          <Chip
                            label="Marbles"
                            color="warning"
                            variant="filled"
                            size="small"
                          />
                        ) : row.ownerType === 3 ? (
                          <Chip
                            label="Rivalz"
                            color="info"
                            variant="filled"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="Unknown"
                            color="error"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row.gameProviderName}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row.images &&
                        row.images.length > 0 &&
                        row.images[0].imageUrl ? (
                          <img
                            src={row.images[0].imageUrl}
                            alt={row.gameProviderName}
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "50px",
                              height: "50px",
                              backgroundColor: "#f5f5f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "4px",
                              color: "#999",
                              fontSize: "10px",
                              textAlign: "center",
                            }}
                          >
                            No Image
                          </Box>
                        )}
                      </TableCell>
                      <TableCell component="th" scope="row" sx={{ width: 300 }}>
                        {row.gameProviderUrl && (
                          <Grid container>
                            <Grid item xs={12}>
                              {`${row.gameProviderUrl}`}
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                variant="text"
                                onClick={() =>
                                  openTestLinkHandler(row.gameProviderId)
                                }
                              >
                                Generate URL
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                      </TableCell>

                      <TableCell component="th" scope="row">
                        {row.gameProviderStatus ? (
                          <>
                            <Iconify
                              icon={"eva:checkmark-circle-2-outline"}
                              sx={{ color: "green" }}
                            />
                          </>
                        ) : (
                          <Iconify
                            icon={"eva:close-circle-outline"}
                            sx={{ color: "red" }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="large"
                          color="inherit"
                          onClick={handleOpenMenu}
                          id={row.gameProviderId}
                        >
                          <Iconify icon={"eva:more-vertical-fill"} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Container>
      <GameProviderStatsDialog
        open={showGameProviderIdStats !== null}
        gameProviderId={showGameProviderIdStats}
        onClose={() => setShowGameProviderIdStats(null)}
      />
      <GameProviderGameConfigDialog
        open={showGameConfigDialog !== null}
        gameProviderId={showGameConfigDialog}
        onClose={() => setShowGameConfigDialog(null)}
        onSuccessHandler={() => {
          setShowGameConfigDialog(null);
          window.location.reload();
        }}
      />
      <Dialog open={testLink !== null} fullWidth={true} maxWidth="md">
        <DialogTitle>Generate Test Link</DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item xs={9} sx={{ m: 0, p: 1 }}>
              <TextField
                label="User ID"
                fullWidth
                value={testLink?.userId ?? ""}
                variant="outlined"
                helperText="Link generates for this user ID. It would expires in 7 days."
                onChange={(e) => {
                  setTestLink((prevState) => ({
                    ...prevState,
                    userId: e.target.value,
                  }));
                }}
              />
            </Grid>
            <Grid item xs={3} sx={{ m: 0, p: 1 }}>
              <Button
                variant="contained"
                disabled={!testLink?.userId}
                fullWidth
                onClick={() => submitGenerateTestUrl()}
                sx={{
                  height: "70%",
                }}
              >
                Generate
              </Button>
            </Grid>
            <Grid item xs={12} sx={{ m: 0, p: 1 }}>
              {testLink?.loading ? (
                <Box
                  sx={{ display: "flex", justifyContent: "center" }}
                  style={{ padding: 20 }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                testLink?.testLink && (
                  <Alert severity="success">{testLink?.testLink}</Alert>
                )
              )}
              {testLink?.error && !testLink?.loading && (
                <Alert severity="error">{testLink?.error}</Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestLink(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            width: 160,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        {!isViewer && (
          <MenuItem onClick={() => openEditProviderDialog(openId)}>
            <Iconify icon={"eva:edit-outline"} sx={{ mr: 2 }} />
            Edit / Creds
          </MenuItem>
        )}

        {!isViewer && (
          <MenuItem onClick={() => openDeleteProviderDialog(openId)}>
            <Iconify icon={"eva:trash-outline"} sx={{ mr: 2 }} />
            Delete
          </MenuItem>
        )}

        <MenuItem onClick={() => setShowGameProviderIdStats(openId)}>
          <Iconify icon={"eva:trending-up-outline"} sx={{ mr: 2 }} />
          Stats
        </MenuItem>
        {openId &&
          rows.length > 0 &&
          rows.find((row) => +row.gameProviderId === +openId).low6GameType >
            0 && (
            <MenuItem onClick={() => openGameConfigDialog(openId)}>
              <Iconify icon={"eva:settings-outline"} sx={{ mr: 2 }} />
              Game Config
            </MenuItem>
          )}
      </Popover>
      <DeleteDialog
        openConfirm={showDeleteProviderDialog}
        setOpenConfirm={setShowDeleteProviderDialog}
        questionIndex={deleteProviderId}
        deleteGameHandler={deleteProviderHandler}
        title={"Delete Message"}
        description={"Are you sure you want to delete this Game Provider?"}
      />
    </>
  );
}
